import { useRef, useEffect, useState } from "react";
import { stars, type Drawing } from "./db/drawing";
import { Tile } from "./GalleryTile";

export type SavedGalleryEntry = {
  thumb: string;
  drawing: Drawing;
  progress: number;
  category?: string;
};

interface Props {
  visible: boolean;
  items: SavedGalleryEntry[];
  onSelect: (entry: SavedGalleryEntry) => void;
  onDelete: (entry: SavedGalleryEntry) => void;
  onClose: () => void;
}

export function SavedGallery({
  visible,
  items,
  onSelect,
  onDelete,
  onClose,
}: Props) {
  const [galleryPage, setGalleryPage] = useState(0);
  const modalRef = useRef<HTMLDivElement>(null);
  const gridRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);

  // close when clicking outside modal
  useEffect(() => {
    if (!visible) return;
    const handleClick = (e: MouseEvent) => {
      // close only when clicking on overlay background
      if (e.target === modalRef.current) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [visible, onClose]);

  const galleryPageSize = 9;

  const visibleItems = items.slice(0, (galleryPage + 1) * galleryPageSize);

  // Ensure-overflow guard: if no overflow yet and there are more items, load more.
  useEffect(() => {
    if (!visible) return;
    const el = gridRef.current;
    if (!el) return;
    if (el.scrollHeight <= el.clientHeight) {
      setGalleryPage((p) => p + 1);
    }
  }, [visible, visibleItems.length]);

  // IO-based infinite scroll (works even before overflow exists).
  useEffect(() => {
    if (!visible) return;
    const root = gridRef.current;
    const sentinel = sentinelRef.current;
    if (!root || !sentinel) return;

    let locked = false;
    const io = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !locked) {
          locked = true;
          setGalleryPage((p) => p + 1);
          queueMicrotask(() => {
            locked = false;
          });
        }
      },
      {
        root,
        rootMargin: "0px 0px 200px 0px",
        threshold: 0,
      }
    );

    io.observe(sentinel);
    return () => io.disconnect();
  }, [visible, visibleItems.length]);

  if (!visible) return null;

  return (
    <div ref={modalRef} className="gallery-modal">
      <div
        ref={gridRef}
        style={{
          width: "92vw",
          maxWidth: 1200,
          height: "85vh",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
          touchAction: "pan-y",
          overscrollBehavior: "contain",
          minHeight: 0,
          minWidth: 0,
          display: "grid",
          gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
          alignContent: "start", // keeps rows packed at the top
          gap: 12,
          padding: 4,
          boxSizing: "border-box",
        }}
      >
        {visibleItems.map((entry, idx) => (
          <Tile
            key={idx}
            entry={entry}
            onClick={() => onSelect(entry)}
            onDelete={() => onDelete(entry)}
            progress={entry.progress}
            stars={
              entry.progress === 1 ? stars(entry.drawing.hints) : undefined
            }
          />
        ))}
        <div ref={sentinelRef} style={{ height: 1 }} />
      </div>

      <div style={{ marginTop: 16, textAlign: "center" }}>
        <button
          onClick={onClose}
          style={{
            background:
              "linear-gradient(180deg, rgba(255,255,255,0.08), rgba(255,255,255,0.03))",
            border: "1px solid rgba(255,255,255,0.18)",
            color: "#fff",
            padding: "8px 16px",
            fontFamily:
              "system-ui, -apple-system, Segoe UI, Roboto, sans-serif",
            fontSize: 14,
            cursor: "pointer",
            borderRadius: 10,
            transition: "transform 120ms ease, box-shadow 120ms ease",
            boxShadow: "0 2px 10px rgba(0,0,0,0.25)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-1px)";
            e.currentTarget.style.boxShadow = "0 6px 18px rgba(0,0,0,0.35)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 2px 10px rgba(0,0,0,0.25)";
          }}
        >
          Close
        </button>
      </div>
    </div>
  );
}
